"use client";

import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    hint?: string;
}

export function PasswordInput({ label, error, hint, className = "", ...props }: PasswordInputProps) {
    const [show, setShow] = useState(false);

    return (
        <div className="w-full space-y-1.5">
            {label && (
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {label}
                    {props.required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    className={`
                        flex h-11 w-full rounded-lg border bg-white dark:bg-slate-900
                        px-3 py-2 pr-10 text-sm text-slate-800 dark:text-slate-100
                        ring-offset-background transition-all duration-200
                        file:border-0 file:bg-transparent file:text-sm file:font-medium
                        placeholder:text-slate-400 dark:placeholder:text-slate-500
                        focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-900
                        disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800
                        ${error
                            ? "border-red-500 focus:ring-red-500/20 focus:border-red-500"
                            : "border-slate-300 dark:border-slate-700"
                        }
                        ${className}
                    `}
                    {...props}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShow((v) => !v)}
                    aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
                >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
            {hint && !error && (
                <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
            )}
            {error && (
                <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                </p>
            )}
        </div>
    );
}
